import type { FC } from "react";

import { resolveModelIconPair } from "@arti/utilities";

import { ProviderIcon, isKnownProvider } from "./ProviderIcon";

interface ModelProviderIconProps {
  modelRef: string;
  /** Size class for the main vendor icon. Badge scales to ~60% of it. */
  className?: string;
  badgeClassName?: string;
}

/**
 * Two-icon model identity: the model vendor's mark with a small corner badge
 * for the serving provider, so DeepSeek-via-OpenRouter and Opus-via-Bedrock
 * are distinguishable from vendor-direct routes at a glance. Vendor-direct
 * refs render only the vendor mark. Transport detail (e.g. "via AWS Bedrock
 * (Anthropic Messages)") rides the hover title.
 */
export const ModelProviderIcon: FC<ModelProviderIconProps> = ({
  badgeClassName = "h-2 w-2",
  className = "h-3.5 w-3.5",
  modelRef,
}) => {
  const { servingProvider, transportLabel, vendor } =
    resolveModelIconPair(modelRef);
  const vendorKnown = vendor !== null && isKnownProvider(vendor);
  const badgeKnown =
    servingProvider !== null && isKnownProvider(servingProvider);

  if (!vendorKnown && !badgeKnown) {
    return null;
  }
  if (!badgeKnown) {
    return <ProviderIcon provider={vendor ?? ""} className={className} />;
  }

  return (
    <span
      className="relative inline-flex shrink-0"
      title={transportLabel ?? undefined}
    >
      {vendorKnown ? (
        <ProviderIcon provider={vendor ?? ""} className={className} />
      ) : (
        <ProviderIcon provider={servingProvider} className={className} />
      )}
      {vendorKnown && (
        <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center rounded-sm bg-white p-px dark:bg-slate-950">
          <ProviderIcon provider={servingProvider} className={badgeClassName} />
        </span>
      )}
    </span>
  );
};
